import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Play, 
  Trash2, 
  ArrowLeft, 
  Calendar,
  Clock,
  Rocket
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSubscription } from '../context/SubscriptionContext';
import UpgradeModal from '../components/UpgradeModal';
import { toast } from 'sonner';
import './RocketGameHub.css';

const RocketGameHub = () => {
  const navigate = useNavigate();
  const { trackPlay, isPremium, canPlayMore, playedGamesCount, maxPlayedGames } = useSubscription();
  const [savedGames, setSavedGames] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const loadSavedGames = () => {
    try {
      const data = localStorage.getItem('rocketGameDatabase');
      const games = data ? JSON.parse(data) : [];
      console.log('📂 Loaded rocket games:', games);
      setSavedGames(games);
    } catch (error) {
      console.error('Error loading saved games:', error);
      toast.error('Failed to load saved games');
    }
  };

  useEffect(() => {
    loadSavedGames();
  }, []);

  const createNewGame = () => {
    navigate('/rocket-game');
  };

  const playGame = async (gameId) => {
    // Check play limits for free users
    if (!isPremium && !canPlayMore) {
      setShowUpgradeModal(true);
      return;
    }

    // Track the play
    const result = await trackPlay(`rocket-game-${gameId}`, 'rocket-game');
    if (!result.success && result.limitReached) {
      setShowUpgradeModal(true);
      return;
    }

    navigate(`/rocket-game?gameId=${gameId}`);
  };

  const deleteGame = (gameId) => {
    try {
      const data = localStorage.getItem('rocketGameDatabase');
      const games = data ? JSON.parse(data) : [];
      const filteredGames = games.filter(g => g.id !== gameId);
      localStorage.setItem('rocketGameDatabase', JSON.stringify(filteredGames));
      
      loadSavedGames();
      setShowDeleteConfirm(null);
      toast.success('Game deleted successfully');
    } catch (error) {
      console.error('Error deleting game:', error);
      toast.error('Failed to delete game');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const isPlayDisabled = !isPremium && !canPlayMore;

  return (
    <div className="rocket-game-hub-page">
      <div className="rocket-game-hub-container">
        <div className="hub-header">
          <Button 
            onClick={() => navigate('/dashboard')} 
            className="back-button"
            variant="ghost"
          >
            <ArrowLeft size={20} />
            Back
          </Button>
          <div className="hub-title">
            <div className="title-with-icon">
              <div className="hub-icon">🚀</div>
              <div>
                <h1>Rocket Quiz Game</h1>
                <p>Blast off to learning!</p>
              </div>
            </div>
          </div>
        </div>

        <motion.div
          className="create-new-section"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Button onClick={createNewGame} className="create-new-btn">
            <Plus size={24} />
            Create New Game
          </Button>
        </motion.div>

        <div className="games-grid">
          {savedGames.map((game, index) => (
              <motion.div
                key={game.id}
                className="game-card"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <div className="game-card-header">
                  <div className="game-icon">🚀</div>
                  <div className="game-info">
                    <h3 className="game-title">{game.title || 'Untitled Game'}</h3>
                    <div className="game-meta">
                      <span className="meta-item">
                        <Calendar size={14} />
                        {formatDate(game.createdAt)}
                      </span>
                      <span className="meta-item">
                        <Clock size={14} />
                        {formatTime(game.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="game-details">
                  <p className="game-question">❓ {game.questions?.length || 0} questions</p>
                </div>

                <div className="game-card-actions">
                  <Button 
                    onClick={() => playGame(game.id)} 
                    className={`play-btn ${isPlayDisabled ? 'disabled' : ''}`}
                    disabled={isPlayDisabled}
                  >
                    <Play size={18} />
                    {isPlayDisabled ? 'Limit Reached' : 'Play'}
                  </Button>
                  <Button 
                    onClick={() => setShowDeleteConfirm(game.id)} 
                    className="delete-btn"
                    variant="ghost"
                  >
                    <Trash2 size={18} />
                  </Button>
                </div>
              </motion.div>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            className="delete-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDeleteConfirm(null)}
          >
            <motion.div
              className="delete-dialog"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3>Delete Game?</h3>
              <p>This action cannot be undone.</p>
              <div className="delete-actions">
                <Button 
                  onClick={() => setShowDeleteConfirm(null)} 
                  variant="ghost"
                >
                  Cancel
                </Button>
                <Button 
                  onClick={() => deleteGame(showDeleteConfirm)} 
                  className="confirm-delete-btn"
                >
                  Delete
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Upgrade Modal */}
      <UpgradeModal 
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        limitType="play"
        currentCount={playedGamesCount}
        maxCount={maxPlayedGames || 3}
      />
    </div>
  );
};

export default RocketGameHub;
